import * as React from "react";
import {useEffect, useRef, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Box, Checkbox, Chip,
    CircularProgress, Fab,
    FormControl,
    Grid,
    InputLabel, ListItemText,
    MenuItem,
    Paper, Tooltip,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {BaseService} from "../../../services/base-service";
import {Download, History, Search, Upload} from "@mui/icons-material";
import {URLS} from "../../../services/app-urls";
import {useNavigate} from "react-router-dom";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {useKeepFilters} from "../../../utils/form-utils";
import {Client} from "../../../models/basic/client";
import Button from "@mui/material/Button";
import {sortListObj} from "../../../utils/list-utils";
import {Form, Formik} from "formik";
import Select from "@mui/material/Select";
import {years} from "../../../models/default/year-month";
import {ImportPo, StatusImportPoLabel} from "../../../models/basic/import-po";
import TableActionComponent from "../../../components/table/table-action.component";
import CollapsibleTableComponent from "../../../components/table/collapsible-table.component";
import {orderByFn} from "../../../utils/order-by";
import AddImportNreCostsComponent from "./add-import-nre-costs";
import {Amortization, AmortizationClient} from "../../../models/basic/amortization";
import {ImportAmortizationFilter, ImportAmortizationService} from "../../../services/import-amortization-service";
import {useFilters} from "../../../filters/base-filter";
import {ImportPoMonitoringService} from "../../../services/import-po-service";
import {usePagination} from "../../../utils/pagination";
import {trackPromise} from "react-promise-tracker";
import {AbstractService} from "../../../services/abstract-service";


const ImportAmortizationComponent = () => {
    const serviceClient = new AbstractService<Client>(URLS.CLIENT);
    const serviceImportPo = new ImportPoMonitoringService();
    const service = new ImportAmortizationService();
    const {filters, updateFilterKeepingParam, updateFilter } = useFilters<ImportAmortizationFilter>(
        { client: "", years: ""},
        ["client", "years"]
    );
    const [poMonitoringList, setPoMonitoringList] = React.useState<PaginatedResult<AmortizationClient>>();
    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<AmortizationClient>(poMonitoringList);
    const expand = ["client", "user"];

    const [orderDirection, setOrderDirection] = React.useState<'asc' | 'desc' | ''>('asc');

    const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);

    const [loading, setLoading] = React.useState(false);

    const [dataSourceYears, setDataSourceYears] = React.useState<{ years: number[] }>();
    const [selectedYears, setSelectedYears] = useState<number[]>([]);
    const [openImportDialog, setOpenImportDialog] = React.useState(false);

    const [listAmortization, setListAmortization] = React.useState<{ [id: number]: Amortization[] }>({});
    const [loadingDetail, setLoadingDetail] = React.useState<{ [id: number]: boolean }>({});
    const [openedDetail, setOpenedDetail] = React.useState<{ [id: number]: boolean }>({});

    const pollingRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        loadClients();
        loadYears();
        searchWithoutPolling();
        initializeYears();
        return () => stopPollingFn();
    }, []);

    const initializeYears = () => {
        const years = filters.years?.split(",");
        setSelectedYears(years?.map(year => parseInt(year)) || []);
    }

    const loadClients = () => {
        serviceClient.readAll().then(response => {
            const sortedData = sortListObj(response.data, "name");
            setDataSourceClient(sortedData);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const loadYears = () => {
        // TODO: corrigir para import-amortization
        serviceImportPo.listYears().then(response => {
            setDataSourceYears(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        stopPollingFn();
        pollingRef.current = setInterval(() => {
            trackPromise(
                service.listClients(filters, expand, pagination).then(response => {
                    setPoMonitoringList(response.data);
                    const noneProcessing = !response.data.results || response.data.results?.every(item => item.last_status !== 1);
                    if (noneProcessing) {
                        stopPollingFn();
                    }
                }).catch((error: any) => {
                    ErrorComponent(error);
                }).finally(() => {
                    setLoading(false);
                })
            )
        }, 10000);

        restartIndex && resetPage();
    };

    const stopPollingFn = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    const searchWithoutPolling = () => {
        setLoading(true);
        service.listClients(filters, expand, pagination).then(response => {
            setPoMonitoringList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        });
    };

    useEffect(() => {
        search();
    }, [pagination, filters.ordering]);

    const customCellRender = (column: string, index: number, row: AmortizationClient) => {
        if (column === "last_status") {
            return <Tooltip title={row.last_error_message} placement="top">
                <Chip size="small"
                             color={StatusImportPoLabel[row.last_status].color as any}
                             label={translate.t(StatusImportPoLabel[row.last_status].label)}
                />
            </Tooltip>
        }
        return row[column as keyof AmortizationClient];
    }

    const customDetailRender = (column: string, index: number, row: Amortization) => {
        if (column === "period") {
            const data = {
                month: row.month,
                period_initial: row.period_initial,
                period_final: row.period_final
            };
            return formatPeriod(data);
        }
        if (column === "status") {
            return <Tooltip title={row.error_message} placement="top">
                 <Chip size="small"
                             color={StatusImportPoLabel[row.status].color as any}
                             label={translate.t(StatusImportPoLabel[row.status].label)}
                />
            </Tooltip>
        }
        return row["month"];
    }

    const renderYears = (selected: any) => {
        const selects: any[] = [];
        selected.map((id: any) => {
            const description: any = years.find(year => id === year.id)?.description;
            selects.push(description);
        });

        return selects.join(", ");
    };

    const download = (row: Amortization) => {
        const serviceAmortization = new BaseService<Amortization>(URLS.IMPORT_AMORTIZATION_DOWNLOAD);
        const params = {
            id: row.id,
        }
        serviceAmortization.loadFile(params).then(response => {
            serviceAmortization.downloadFile(response);
        }).catch((error: any) => {
            ErrorComponent(error);
        })
    }

    const layoutDetail = (row: ImportPo) => {
        if (loadingDetail[row.id]) {
            return <Box padding={1}>
                <CircularProgress size="30px" />
            </Box>
        }
        return (
            <TablePaginationComponent
                key={row.id}
                state={listAmortization[row.id] || []}
                displayedColumnsShow={["period", "import_date", "user", "import_status", "action"]}
                displayedColumns={["period", "created_at", "user.name", "status", "action"]}
                dateColumns={["created_at"]}
                customCellRender={customDetailRender}
                secondaryLayout={true}
                showActions={["download"]}
                downloadFn={download}
                customColumns={["period", "status"]}
                loading={loadingDetail[row.id]}
            />
        )
    }

    const loadDetail = (row: Amortization) => {
        setOpenedDetail({...openedDetail, [row.id]: !openedDetail[row.id]});

        if (!openedDetail[row.id]) {
            setListAmortization({...listAmortization, [row.id]: []});
            setLoadingDetail({...loadingDetail, [row.id]: true});
            const filter = { client: row.id};
            service.readAll(filter, expand).then(response => {
                setListAmortization({...listAmortization, [row.id]: response.data});
            }).catch((error: any) => {
                ErrorComponent(error);
            }).finally(() => {
                setLoadingDetail({...loadingDetail, [row.id]: false});
            });
        }
    }

    const closeAllCollapses = () => {
        setOpenedDetail({});
    }

    function formatPeriod(data: { month: number; period_initial: string; period_final: string }): string {
        const months = [
            "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
            "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"
        ];

        const monthName = months[data.month - 1];

        const formatDate = (date: string): string => {
            const [year, month, day] = date.split("-");
            return `${day}/${month}/${year}`;
        };

        const periodInitialFormatted = formatDate(data.period_initial);
        const periodFinalFormatted = formatDate(data.period_final);

        return `${monthName} - ${periodInitialFormatted} a ${periodFinalFormatted}`;
    }

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("import_amortization")}
                </Typography>
            </div>
            <div className="floating-button" style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<Upload />}
                    onClick={() => {
                        setOpenImportDialog(true);
                    }}>{translate.t("import_nre_costs").toUpperCase()}
                </Button>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    searchWithoutPolling();
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"} width="50%">
                                                <Grid size={2}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Select
                                                            name="client"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.client || "0"}
                                                            onChange={(event) => updateFilterKeepingParam("client", event.target.value as string )}
                                                        >
                                                            <MenuItem key="0" value="0">
                                                                {translate.t('all')}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={3}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("year")}
                                                        </InputLabel>
                                                        <Select
                                                            name="year"
                                                            label={translate.t("year")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={selectedYears}
                                                            onChange={(event: any) => {
                                                                const validValues = event.target.value
                                                                    .map((value: any) => Number(value))
                                                                    .filter((value: number) => Number.isFinite(value));
                                                                setSelectedYears(validValues);
                                                                updateFilterKeepingParam("years", validValues.join(","));
                                                            }}
                                                            multiple
                                                            renderValue={(selected) => renderYears(selected)}
                                                        >
                                                            {dataSourceYears?.years.map((year: any) => (
                                                                <MenuItem key={year} value={year}>
                                                                    <Checkbox checked={selectedYears.includes(year)}/>
                                                                    <ListItemText primary={year}/>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid >
                                                    <Fab
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                            </Grid>
                                            {loading && <CircularProgress size="30px" />}
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <CollapsibleTableComponent
                    state={dataSource}
                    displayedColumnsShow={["client", "latest_importation", "import_status"]}
                    displayedColumns={["name", "last_import_date", "last_status"]}
                    dateColumns={["last_import_date"]}
                    orderByColumns={["name"]}
                    orderBy={filters.ordering}
                    orderByFn={(column: string, orderDirection: any) => {
                        orderByFn(column, orderDirection, filters.ordering, setOrderDirection, updateFilterKeepingParam);
                    }}
                    orderDirection={orderDirection}
                    actionsComponent={TableActionComponent}
                    childrenComponent={layoutDetail}
                    count={poMonitoringList?.count}
                    next={poMonitoringList?.next}
                    previous={poMonitoringList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loading}
                    loadOnOpen={loadDetail}
                    customCellRender={customCellRender}
                    customColumns={["last_status"]}
                    openControl={openedDetail}
                />
            </Paper>

            <AddImportNreCostsComponent
                openDialog={openImportDialog}
                handleCloseDialog={() => {
                    searchWithoutPolling();
                    search(true);
                    setOpenImportDialog(false);
                    closeAllCollapses();
                }}
                dataSourceClient={dataSourceClient}
            />
        </section>
    );
};

export default ImportAmortizationComponent;
